/* eslint-disable no-unused-vars */
import {
  isAdmin,
  isIctAdministrator,
  isContributor,
  isViewer,
  isExternalContributor,
  hasProfileEnabled,
  isAdministrator,
} from './Roles'
import * as R from 'ramda'
import { isFrecUser } from '../FRec/Roles'
import { isFtaoAdministrator, isFtaoContributor, isFtaoUser, isFtaoViewer } from '../Ftao/Roles'

const isRecordOwner = (fk) => (user, record) => user.id === record[fk]

// each perm function receives two arguments:
// - user: the logged in user
// - record | URI matches: the record to check for permissions (data table actions) or the URI params (router)

export const DashboardPermissions = {
  read: R.T,
}

export const DeviceImagesArchivePermissions = {
  read: R.anyPass([isAdmin, isContributor, isViewer]),
  download: R.anyPass([isAdmin, isContributor, isViewer]),
  updateMaturity: isIctAdministrator,
}

export const VehiclesPermissions = {
  read: R.T,
  create: isAdmin,
  update: (user, vehicle) => {
    return isAdmin(user) || (isContributor(user) && vehicle.ownerId === user.id)
  },
  delete: isAdmin,
  history: R.anyPass([isAdmin, isContributor, isViewer]),
  sendImage: isAdmin,
  sendPackage: R.either(isAdmin, isContributor),
  sendCommand: R.either(isAdmin, isContributor),
  enableDap: R.both(isAdmin, R.compose(R.not, R.pathEq(['domain', 'code'], 'FPT'))),
  readModels: R.either(isAdmin, isContributor),
}

export const VehiclesFleetsPermissions = {
  read: R.anyPass([isAdmin, isContributor, isViewer]),
  create: isAdmin,
  update: isAdmin,
  delete: isAdmin,
  sendImage: isAdmin,
  sendPackage: R.either(isAdmin, isContributor),
  sendCommand: R.either(isAdmin, isContributor),
}

export const DevicesPermissions = {
  read: R.T,
  create: isAdmin,
  update: (user, device) => {
    return isAdmin(user) || (isContributor(user) && device.ownerId === user.id)
  },
  delete: isAdmin,
  history: R.anyPass([isAdmin, isContributor, isViewer]),
  activate: isAdmin,
  associateVehicle: isAdmin,
  generateToken: isAdmin,
  checkConnection: R.either(isAdmin, isContributor),
  sendImage: isAdmin,
  sendPackage: R.either(isAdmin, isContributor),
  sendCommand: R.either(isAdmin, isContributor),
  viewSimInfo: R.either(isAdmin, isContributor),
  readModels: R.either(isAdmin, isContributor),
}

export const DevicesFleetsPermissions = {
  read: R.anyPass([isAdmin, isContributor, isViewer]),
  create: isAdmin,
  update: isAdmin,
  delete: isAdmin,
  sendPackage: R.either(isAdmin, isContributor),
  sendCommand: R.either(isAdmin, isContributor),
  sendImage: isAdmin,
}

export const UsersPermissions = {
  read: isAdmin,
  create: isIctAdministrator,
  update: isIctAdministrator,
  delete: isIctAdministrator,
}

export const ResourcesPermissions = {
  read: R.anyPass([isAdmin, isContributor, isExternalContributor]),
  create: R.anyPass([isAdmin, isContributor, isExternalContributor]),
  update: R.anyPass([isAdmin, isContributor, isExternalContributor]),
  delete: R.anyPass([isAdmin, isContributor, isExternalContributor]),
  uploadDefault: isAdmin,
  download: R.anyPass([isAdmin, isContributor, isExternalContributor]),
  updateBusType: R.anyPass([isAdmin, isContributor, isExternalContributor]),
}

export const ConfiguratorPermissions = {
  read: R.anyPass([isAdmin, isContributor, isExternalContributor]),
  create: R.either(isAdmin, isContributor),
  update: R.anyPass([isAdmin, isContributor, isExternalContributor]), // tags check is done on the server, external contributo only sees configurations he can handle
  updateInternalCanBus: R.either(isAdmin, isContributor),
  downloadPublished: R.anyPass([isAdmin, isContributor, isExternalContributor]), // tags check is done on the server, external contributo only sees configurations he can handle
  delete: (user, configuration) => {
    if (isAdmin(user) || isContributor(user)) return true
    if (isExternalContributor(user) && isRecordOwner('ownerId')(user, configuration)) return true
    return false
  },
  assignExternalTags: R.either(isAdmin, isContributor),
  canSettings: {
    read: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('canSetting')),
      R.both(isContributor, hasProfileEnabled('canSetting')),
      isExternalContributor,
    ]),
    create: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('canSetting')),
      R.both(isContributor, hasProfileEnabled('canSetting')),
      isExternalContributor,
    ]),
    update: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('canSetting')),
      R.both(isContributor, hasProfileEnabled('canSetting')),
      isExternalContributor,
    ]),
    delete: (user, busConfiguration) => {
      if (
        isIctAdministrator(user) ||
        (isAdministrator(user) && hasProfileEnabled('canSetting')(user)) ||
        (isContributor(user) && hasProfileEnabled('canSettings')(user))
      )
        return true
      if (isExternalContributor(user) && busConfiguration?.createdBy?.toLowerCase() === user.email.toLowerCase())
        return true
      return false
    },
  },
  dtcSettings: {
    read: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('canSetting')),
      R.both(isContributor, hasProfileEnabled('canSetting')),
      isExternalContributor,
    ]),
    create: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('canSetting')),
      R.both(isContributor, hasProfileEnabled('canSetting')),
      isExternalContributor,
    ]),
    delete: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('canSetting')),
      R.both(isContributor, hasProfileEnabled('canSetting')),
      isExternalContributor,
    ]),
  },
  hardwareSettings: {
    read: R.either(isAdmin, isContributor),
    create: R.either(isAdmin, isContributor),
    update: R.either(isAdmin, isContributor),
    delete: R.either(isAdmin, isContributor),
  },
  a2lSignalConfigurations: {
    // DONE
    read: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('a2lSignalConfiguration')),
      R.both(isContributor, hasProfileEnabled('a2lSignalConfiguration')),
      isExternalContributor,
    ]),
    create: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('a2lSignalConfiguration')),
      R.both(isContributor, hasProfileEnabled('a2lSignalConfiguration')),
      isExternalContributor,
    ]),
    update: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('a2lSignalConfiguration')),
      R.both(isContributor, hasProfileEnabled('a2lSignalConfiguration')),
      isExternalContributor,
    ]),
    delete: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('a2lSignalConfiguration')),
      R.both(isContributor, hasProfileEnabled('a2lSignalConfiguration')),
      isExternalContributor,
    ]),
    uploadFile: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('a2lSignalConfiguration')),
      R.both(isContributor, hasProfileEnabled('a2lSignalConfiguration')),
      isExternalContributor,
    ]),
  },
  signalAlias: {
    read: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('signalAlias')),
      R.both(isContributor, hasProfileEnabled('signalAlias')),
      isExternalContributor,
    ]),
    create: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('signalAlias')),
      R.both(isContributor, hasProfileEnabled('signalAlias')),
      isExternalContributor,
    ]),
    update: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('signalAlias')),
      R.both(isContributor, hasProfileEnabled('signalAlias')),
      isExternalContributor,
    ]),
    delete: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('signalAlias')),
      R.both(isContributor, hasProfileEnabled('signalAlias')),
      isExternalContributor,
    ]),
  },
  miniVpr: {
    // DONE
    read: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('miniVpr')),
      R.both(isContributor, hasProfileEnabled('miniVpr')),
    ]),
    create: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('miniVpr')),
      R.both(isContributor, hasProfileEnabled('miniVpr')),
    ]),
    update: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('miniVpr')),
      R.both(isContributor, hasProfileEnabled('miniVpr')),
    ]),
    delete: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('miniVpr')),
      R.both(isContributor, hasProfileEnabled('miniVpr')),
    ]),
  },
  canLogger: {
    read: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('canLogger')),
      R.both(isContributor, hasProfileEnabled('canLogger')),
      isExternalContributor,
    ]),
    create: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('canLogger')),
      R.both(isContributor, hasProfileEnabled('canLogger')),
      isExternalContributor,
    ]),
    update: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('canLogger')),
      R.both(isContributor, hasProfileEnabled('canLogger')),
      isExternalContributor,
    ]),
    delete: (user, logger) => {
      if (
        isIctAdministrator(user) ||
        (isAdministrator(user) && hasProfileEnabled('canLogger')(user)) ||
        (isContributor(user) && hasProfileEnabled('canLogger')(user))
      )
        return true
      if (isExternalContributor(user) && logger.createdBy.toLowerCase() === user.email.toLowerCase()) return true
      return false
    },
  },
  signalLogger: {
    read: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('signalLogger')),
      R.both(isContributor, hasProfileEnabled('signalLogger')),
      isExternalContributor,
    ]),
    create: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('signalLogger')),
      R.both(isContributor, hasProfileEnabled('signalLogger')),
      isExternalContributor,
    ]),
    update: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('signalLogger')),
      R.both(isContributor, hasProfileEnabled('signalLogger')),
      isExternalContributor,
    ]),
    delete: (user, logger) => {
      if (
        isIctAdministrator(user) ||
        (isAdministrator(user) && hasProfileEnabled('canLogger')(user)) ||
        (isContributor(user) && hasProfileEnabled('canLogger')(user))
      )
        return true
      if (isExternalContributor(user) && logger.createdBy.toLowerCase() === user.email.toLowerCase()) return true
      return false
    },
  },
  triggers: {
    read: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('trigger')),
      R.both(isContributor, hasProfileEnabled('trigger')),
      isExternalContributor,
    ]),
    create: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('trigger')),
      R.both(isContributor, hasProfileEnabled('trigger')),
      isExternalContributor,
    ]),
    update: (user, trigger) => {
      if (
        isIctAdministrator(user) ||
        (isAdministrator(user) && hasProfileEnabled('trigger')(user)) ||
        (isContributor(user) && hasProfileEnabled('trigger')(user))
      )
        return true
      if (isExternalContributor(user) && trigger.createdBy.toLowerCase() === user.email.toLowerCase()) return true
      return false
    },
    delete: (user, trigger) => {
      if (
        isIctAdministrator(user) ||
        (isAdministrator(user) && hasProfileEnabled('trigger')(user)) ||
        (isContributor(user) && hasProfileEnabled('trigger')(user))
      )
        return true
      if (isExternalContributor(user) && trigger.createdBy.toLowerCase() === user.email.toLowerCase()) return true
      return false
    },
    clone: (user, trigger) => {
      if (
        isIctAdministrator(user) ||
        (isAdministrator(user) && hasProfileEnabled('trigger')(user)) ||
        (isContributor(user) && hasProfileEnabled('trigger')(user))
      )
        return true
      if (isExternalContributor(user) && trigger.createdBy.toLowerCase() === user.email.toLowerCase()) return true
      return false
    },
  },
  rtm: {
    read: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('rtm')),
      R.both(isContributor, hasProfileEnabled('rtm')),
    ]),
    create: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('rtm')),
      R.both(isContributor, hasProfileEnabled('rtm')),
    ]),
    update: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('rtm')),
      R.both(isContributor, hasProfileEnabled('rtm')),
    ]),
    delete: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('rtm')),
      R.both(isContributor, hasProfileEnabled('rtm')),
    ]),
  },
  rtmLoggers: {
    read: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('rtm')),
      R.both(isContributor, hasProfileEnabled('rtm')),
    ]),
    create: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('rtm')),
      R.both(isContributor, hasProfileEnabled('rtm')),
    ]),
  },
  rtmTrip: {
    read: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('rtm')),
      R.both(isContributor, hasProfileEnabled('rtm')),
    ]),
    create: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('rtm')),
      R.both(isContributor, hasProfileEnabled('rtm')),
    ]),
    update: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('rtm')),
      R.both(isContributor, hasProfileEnabled('rtm')),
    ]),
    delete: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('rtm')),
      R.both(isContributor, hasProfileEnabled('rtm')),
    ]),
  },
  rtmEvent: {
    read: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('rtm')),
      R.both(isContributor, hasProfileEnabled('rtm')),
    ]),
    create: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('rtm')),
      R.both(isContributor, hasProfileEnabled('rtm')),
    ]),
    update: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('rtm')),
      R.both(isContributor, hasProfileEnabled('rtm')),
    ]),
    delete: (user, rtmEvent) => {
      if (
        isIctAdministrator(user) ||
        (isAdministrator(user) && hasProfileEnabled('trigger')(user)) ||
        (isContributor(user) && hasProfileEnabled('trigger')(user))
      )
        return true
      // if (isExternalContributor(user) && rtmEvent.createdBy === user.email) return true
      return false
    },
  },
  missionProfile: {
    read: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('missionProfile')),
      R.both(isContributor, hasProfileEnabled('missionProfile')),
    ]),
    create: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('missionProfile')),
      R.both(isContributor, hasProfileEnabled('missionProfile')),
    ]),
    update: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('missionProfile')),
      R.both(isContributor, hasProfileEnabled('missionProfile')),
    ]),
    delete: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('missionProfile')),
      R.both(isContributor, hasProfileEnabled('missionProfile')),
    ]),
  },
  missionProfileLimit: {
    read: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('missionProfile')),
      R.both(isContributor, hasProfileEnabled('missionProfile')),
    ]),
    create: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('missionProfile')),
      R.both(isContributor, hasProfileEnabled('missionProfile')),
    ]),
    update: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('missionProfile')),
      R.both(isContributor, hasProfileEnabled('missionProfile')),
    ]),
    delete: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('missionProfile')),
      R.both(isContributor, hasProfileEnabled('missionProfile')),
    ]),
  },
  missionProfileAccumulation: {
    read: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('missionProfile')),
      R.both(isContributor, hasProfileEnabled('missionProfile')),
    ]),
    create: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('missionProfile')),
      R.both(isContributor, hasProfileEnabled('missionProfile')),
    ]),
    update: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('missionProfile')),
      R.both(isContributor, hasProfileEnabled('missionProfile')),
    ]),
    delete: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('missionProfile')),
      R.both(isContributor, hasProfileEnabled('missionProfile')),
    ]),
  },
  missionProfileItem: {
    read: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('missionProfile')),
      R.both(isContributor, hasProfileEnabled('missionProfile')),
    ]),
    create: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('missionProfile')),
      R.both(isContributor, hasProfileEnabled('missionProfile')),
    ]),
    update: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('missionProfile')),
      R.both(isContributor, hasProfileEnabled('missionProfile')),
    ]),
    delete: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('missionProfile')),
      R.both(isContributor, hasProfileEnabled('missionProfile')),
    ]),
  },
  dm1: {
    read: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('vehicleDiagnostic')),
      R.both(isContributor, hasProfileEnabled('vehicleDiagnostic')),
      isExternalContributor,
    ]),
    create: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('vehicleDiagnostic')),
      R.both(isContributor, hasProfileEnabled('vehicleDiagnostic')),
    ]),
    update: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('vehicleDiagnostic')),
      R.both(isContributor, hasProfileEnabled('vehicleDiagnostic')),
      isExternalContributor,
    ]),
    delete: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('vehicleDiagnostic')),
      R.both(isContributor, hasProfileEnabled('vehicleDiagnostic')),
    ]),
  },
  infoReader: {
    read: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('infoReader')),
      R.both(isContributor, hasProfileEnabled('infoReader')),
      isExternalContributor,
    ]),
    create: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('infoReader')),
      R.both(isContributor, hasProfileEnabled('infoReader')),
    ]),
    update: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('infoReader')),
      R.both(isContributor, hasProfileEnabled('infoReader')),
      isExternalContributor,
    ]),
    delete: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('infoReader')),
      R.both(isContributor, hasProfileEnabled('infoReader')),
    ]),
  },
  insights: {
    read: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('insights')),
      R.both(isContributor, hasProfileEnabled('insights')),
    ]),
    create: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('insights')),
      R.both(isContributor, hasProfileEnabled('insights')),
    ]),
    update: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('insights')),
      R.both(isContributor, hasProfileEnabled('insights')),
    ]),
    delete: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('insights')),
      R.both(isContributor, hasProfileEnabled('insights')),
    ]),
  },
  blueTag: {
    read: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('blueTag')),
      R.both(isContributor, hasProfileEnabled('blueTag')),
      isExternalContributor,
    ]),
    create: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('blueTag')),
      R.both(isContributor, hasProfileEnabled('blueTag')),
    ]),
    update: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('blueTag')),
      R.both(isContributor, hasProfileEnabled('blueTag')),
      isExternalContributor,
    ]),
    delete: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('blueTag')),
      R.both(isContributor, hasProfileEnabled('blueTag')),
    ]),
  },
  digitalTwin: {
    read: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('digitalTwin')),
      R.both(isContributor, hasProfileEnabled('digitalTwin')),
    ]),
    create: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('digitalTwin')),
      R.both(isContributor, hasProfileEnabled('digitalTwin')),
    ]),
    update: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('digitalTwin')),
      R.both(isContributor, hasProfileEnabled('digitalTwin')),
    ]),
    delete: R.anyPass([
      isIctAdministrator,
      R.both(isAdmin, hasProfileEnabled('digitalTwin')),
      R.both(isContributor, hasProfileEnabled('digitalTwin')),
    ]),
  },
}

export const DataPermissions = {
  applicationFiles: {
    read: R.T,
    reprocess: isAdmin,
    download: R.either(isAdmin, isContributor),
    manualUpload: R.anyPass([isAdmin, isContributor, isExternalContributor]),
    storageUpload: R.anyPass([isAdmin, isContributor]),
    hide: isIctAdministrator,
    removeFromDap: isAdmin,
    forceDapIngestion: isAdmin,
    resendToFtp: isIctAdministrator,
  },
  canLogger: {
    read: R.T,
    download: R.T,
    detail: R.T,
  },
  signalLogger: {
    read: R.T,
    download: R.T,
    detail: R.T,
  },
  rtm: {
    trip: {
      read: R.anyPass([isAdmin, isContributor, isViewer]),
      detail: R.anyPass([isAdmin, isContributor, isViewer]),
      download: R.anyPass([isAdmin, isContributor, isViewer]),
    },
    event: {
      read: R.anyPass([isAdmin, isContributor, isViewer]),
      detail: R.anyPass([isAdmin, isContributor, isViewer]),
      download: R.anyPass([isAdmin, isContributor, isViewer]),
    },
    dtc: {
      read: R.anyPass([isAdmin, isContributor, isViewer]),
      detail: R.anyPass([isAdmin, isContributor, isViewer]),
      download: R.anyPass([isAdmin, isContributor, isViewer]),
    },
  },
  miniVpr: {
    read: R.anyPass([isAdmin, isContributor, isViewer]),
    detail: R.anyPass([isAdmin, isContributor, isViewer]),
    download: R.anyPass([isAdmin, isContributor, isViewer]),
  },
  missionProfile: {
    read: R.anyPass([isAdmin, isContributor, isViewer]),
    detail: R.anyPass([isAdmin, isContributor, isViewer]),
    download: R.anyPass([isAdmin, isContributor, isViewer]),
    createAggregation: R.anyPass([isAdmin, isContributor]),
    reprocessAggregation: R.anyPass([isAdmin, isContributor]),
  },
  vehicleDiagnostic: {
    read: R.T,
    download: R.T,
    detail: R.T,
  },
  infoReader: {
    read: R.T,
    download: R.T,
    detail: R.T,
  },
  tdms: {
    read: R.T,
    detail: R.T,
    download: R.T,
  },
  documents: {
    read: R.anyPass([isAdmin, isContributor, isViewer]),
    upload: R.either(isAdmin, isContributor),
    delete: R.either(isAdmin, isContributor),
  },
  images: {
    read: R.anyPass([isAdmin, isContributor, isViewer]),
    // cloneBundle: R.either(isAdmin, isContributor),
    // downloadBundle: R.either(isAdmin, isContributor),
    // deleteBundle: R.either(isAdmin, isContributor),
    // deleteImage: R.either(isAdmin, isContributor),
    // sendToSupervisely: R.either(isAdmin, isContributor),
    // syncToSupervisely: R.either(isAdmin, isContributor),
    // mergeBundles: R.either(isAdmin, isContributor),
    // downloadSuperviselyImages: R.either(isAdmin, isContributor),
    // GotoSupervisely: R.either(isAdmin, isContributor),
    // readDatasets: R.either(isAdmin, isContributor),
    // createDataset: R.either(isAdmin, isContributor),
    // updateDataset: R.either(isAdmin, isContributor),
    // deleteDataset: R.either(isAdmin, isContributor),
    // downloadDataset: R.either(isAdmin, isContributor),

    cloneBundle: R.anyPass([isAdmin, isContributor, isViewer]),
    downloadBundle: R.anyPass([isAdmin, isContributor, isViewer]),
    deleteBundle: R.anyPass([isAdmin, isContributor, isViewer]),
    deleteImage: R.anyPass([isAdmin, isContributor, isViewer]),
    sendToSupervisely: R.anyPass([isAdmin, isContributor, isViewer]),
    syncToSupervisely: R.anyPass([isAdmin, isContributor, isViewer]),
    mergeBundles: R.anyPass([isAdmin, isContributor, isViewer]),
    downloadSuperviselyImages: R.anyPass([isAdmin, isContributor, isViewer]),
    GotoSupervisely: R.anyPass([isAdmin, isContributor, isViewer]),
    readDatasets: R.anyPass([isAdmin, isContributor, isViewer]),
    createDataset: R.anyPass([isAdmin, isContributor, isViewer]),
    updateDataset: R.anyPass([isAdmin, isContributor, isViewer]),
    deleteDataset: R.anyPass([isAdmin, isContributor, isViewer]),
    downloadDataset: R.anyPass([isAdmin, isContributor, isViewer]),
  },
  dataAnalytics: {
    read: R.both(R.anyPass([isAdmin, isContributor, isViewer]), R.compose(R.not, R.pathEq(['domain', 'code'], 'FPT'))),
    create: R.both(R.either(isAdmin, isContributor), R.compose(R.not, R.pathEq(['domain', 'code'], 'FPT'))),
    cancel: R.both(
      R.either(isAdmin, (user, dap) => isContributor(user) && user.id === dap.ownerId),
      R.compose(R.not, R.pathEq(['domain', 'code'], 'FPT')),
    ),
    delete: R.both(
      R.either(isAdmin, (user, dap) => isContributor(user) && user.id === dap.ownerId),
      R.compose(R.not, R.pathEq(['domain', 'code'], 'FPT')),
    ),
    clone: R.both(
      R.either(isAdmin, (user, dap) => isContributor(user) && user.id === dap.ownerId),
      R.compose(R.not, R.pathEq(['domain', 'code'], 'FPT')),
    ),
  },
}

export const ReportPermissions = {
  read: R.both(R.anyPass([isAdmin, isContributor, isViewer]), R.compose(R.not, R.pathEq(['domain', 'code'], 'FPT'))),
  create: R.both(R.either(isAdmin, isContributor), R.compose(R.not, R.pathEq(['domain', 'code'], 'FPT'))),
  update: R.both(
    R.either(isAdmin, (user, dap) => isContributor(user) && user.id === dap.ownerId),
    R.compose(R.not, R.pathEq(['domain', 'code'], 'FPT')),
  ),
  delete: R.both(
    R.either(isAdmin, (user, dap) => isContributor(user) && user.id === dap.ownerId),
    R.compose(R.not, R.pathEq(['domain', 'code'], 'FPT')),
  ),
  submit: R.both(
    R.either(isAdmin, (user, dap) => isContributor(user) && user.id === dap.ownerId),
    R.compose(R.not, R.pathEq(['domain', 'code'], 'FPT')),
  ),
}

export const BlueTagPermissions = {
  read: R.anyPass([isAdmin, isContributor, isViewer]),
  create: R.either(isAdmin, isContributor),
  update: isIctAdministrator,
  delete: isIctAdministrator,
  upgradeInternalDbc: isIctAdministrator,
}

export const FrecPermissions = {
  access: isFrecUser,
}

export const FtaoPermissions = {
  access: isFtaoUser,
  plans: {
    read: R.anyPass([isFtaoAdministrator, isFtaoContributor, isFtaoViewer]),
    create: R.either(isFtaoAdministrator, isFtaoContributor),
    update: R.either(isFtaoAdministrator, isFtaoContributor),
    delete: R.either(isFtaoAdministrator, isFtaoContributor),
    refresh: R.either(isFtaoAdministrator, isFtaoContributor),
    events: R.either(isFtaoAdministrator, isFtaoContributor),
    eventsDelete: R.either(isFtaoAdministrator, isFtaoContributor),
    eventsUpdate: R.either(isFtaoAdministrator, isFtaoContributor),
    eventsCreate: R.either(isFtaoAdministrator, isFtaoContributor),
  },
}

export const DigitalTwinPermissions = {
  access: R.T,
  models: {
    read: R.T,
    readSignals: R.T,
    create: R.either(isAdmin, isContributor),
    update: R.either(isAdmin, isContributor),
    delete: R.either(isAdmin, isContributor),
  },
  instances: {
    read: R.T,
    readSignals: R.T,
    create: R.either(isAdmin, isContributor),
    update: R.either(isAdmin, isContributor),
    delete: R.either(isAdmin, isContributor),
    startPod: R.either(isAdmin, isContributor),
    stopPod: R.either(isAdmin, isContributor),
    startModel: R.either(isAdmin, isContributor),
    stopModel: R.either(isAdmin, isContributor),
    resetModel: R.either(isAdmin, isContributor),
  },
}

export const TestCellsPermissions = {
  access: R.anyPass([isAdmin, isContributor, isViewer]),
  testCell: {
    read: R.anyPass([isAdmin, isContributor, isViewer]),
    associateVehicle: R.either(isAdmin, isContributor),
    associateDevice: R.either(isAdmin, isContributor),
  },
  testRevision: {
    read: R.anyPass([isAdmin, isContributor, isViewer]),
    create: R.anyPass([isAdmin, isContributor, isViewer]),
    update: R.anyPass([isAdmin, isContributor, isViewer]),
  },
}

export const VehicleComponentsPermissions = {
  access: R.anyPass([isAdmin, isContributor, isViewer]),
  vehicleComponent: {
    read: R.anyPass([isAdmin, isContributor, isViewer]),
    create: R.either(isAdmin, isContributor),
    update: R.either(isAdmin, isContributor),
    delete: R.either(isAdmin, isContributor),
  },
}
